import { ZipFile } from "@utils/zip-file";
import { ReadLsDataV2Response, LsRootObject, LsScan } from "@src/api/stagingarea-api/stagingarea-api-types";
import { getEntitiesPath } from "@utils/capture-tree/capture-tree-utils";
import { FileExt } from "@utils/file-ext";
import { getScanAttributesFromFile } from "@pages/data-management/import-data/attribute-container";
import { FOCUS_SCANNER_SUPPORTED_MIN_VERSION, getFocusVersion } from "@pages/data-management/import-data/import-data-utils";

const MS_PER_SECOND = 1_000;

/**
 * FocusZipFile class extends ZipFile to include scanId and timestamp properties.
 */
export class FocusZipFile extends ZipFile {
  /** Original ScanID */
  scanId: string;
  /** Timestamp in milliseconds */
  timestamp: number;

  constructor(files: (File | FileExt)[], name: string, scanId: string, timestampSeconds: number) {
    super(files, name);
    this.scanId = scanId;
    // Convert timestamp from seconds to milliseconds
    // to match the expected format in the LsScan object.
    this.timestamp = timestampSeconds * MS_PER_SECOND;
  }
}

/**
 * Creates a dummy LsDataV2 object from a list of FocusZipFiles
 */
export function createFocusDummyLsDataV2(focusFiles: FocusZipFile[]): ReadLsDataV2Response {
  // Minimal root object
  const rootObject: LsRootObject = {
    name: "RootObject",
    uuid: "00000000-0000-0000-0000-000000000000",
    revision: "0",
    treeLevel: 0,
    treePath: "root",
    ancestorUuids: [],
  };

  // Create LsScan objects for each FocusZipFile
  const scans: LsScan[] = focusFiles.map((focusFile) => {
    const flsName = focusFile.name.replace(/\.zip$/, "");
    return {
      uuid: focusFile.scanId,
      // This fallback will make sure, the scan is added to the root.
      parentUuid: "root",
      name: flsName,
      description: "",
      recordingTime: focusFile.timestamp ? new Date(focusFile.timestamp).toISOString() : undefined,
      files: [
        {
          path: flsName,
        },
      ],
      treeLevel: 1,
      treePath: getEntitiesPath([rootObject.name, flsName]),
      ancestorUuids: [rootObject.uuid],
    };
  });

  // Map from filename (lowercase) to scan
  const scansByFilename: Record<string, LsScan> = {};
  scans.forEach((scan) => {
    scansByFilename[scan.name.toLowerCase()] = scan;
  });

  // Map from uuid to scan
  const scansByUuid: Record<string, LsScan> = {};
  scans.forEach((scan) => {
    scansByUuid[scan.uuid] = scan;
  });

  return {
    rootObject,
    projects: [],
    clusters: [],
    scans,
    edges: [],
    maxTreeLevel: 1,
    treeObjectsByUuid: {
      ...scansByUuid,
    },
    clustersByUuid: {},
    scansByUuid,
    scansByFilename,
  };
}

/**
 * Get a Focus scan
 * Expects a .fls folder that contains a bunch of files belonging to a focus scan.
 *
 * @param files
 * @returns Focus Scan as a FocusZipFile with the extension .fls.zip
 */
export async function getFocusZipFile(files: FileExt[]): Promise<FocusZipFile | undefined> {
  const flsFiles = files.filter((file) => file.name.endsWith(".fls"));
  if (flsFiles.length === 0) {
    return undefined;
  }
  const flsFile = flsFiles[0];

  const versionFile = files.filter((file) => file.name === "Version");
  if (versionFile.length !== 1) {
    return undefined;
  }
  const version = await getFocusVersion(versionFile[0]);
  if (!(version >= FOCUS_SCANNER_SUPPORTED_MIN_VERSION)) {
    return undefined;
  }flsFile;

  // TODO: Check if the Focus files have the expected structure. Follow-up [TF-2089]

  const scan0File = files.find((file) => file.webkitRelativePath === "Parameters/Scan0");
  if (!scan0File) {
    return undefined;
  }
  const scanAttributes = await getScanAttributesFromFile(scan0File);

  if (!scanAttributes.ScanId) {
    return undefined;
  }

  // Return the FocusZipFile object which is a zipped version of the .fls folder
  return new FocusZipFile(
    files, `${flsFile.name}.zip`,
    scanAttributes.ScanId,
    scanAttributes.timestamp ?? 0
  );
}
